/*
 * Full-page store locator.
 *
 * All search logic — loading the data files, postal lookup, haversine,
 * radius filtering — comes from assets/store-search.js, shared with the header
 * dropdown in store-selector.js. This file is layout and interaction only.
 *
 * The page also renders every store server-side (see sections/store-locator.liquid)
 * so the list is crawlable and works without JavaScript. Searching filters and
 * re-orders that same set; it never replaces the page with a JS-only list.
 *
 * Results are announced on a `locator:results` event. store-locator-map.js
 * listens for it and re-pins the map. The event is one-way on purpose: this
 * file never waits for the map, never checks whether one exists, and behaves
 * identically when the map is switched off, blocked, or failing to load.
 */
class StoreLocator extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-locator-form]');
    this.input = this.querySelector('[data-locator-postal]');
    this.useLocation = this.querySelector('[data-locator-geolocate]');
    this.status = this.querySelector('[data-locator-status]');
    this.results = this.querySelector('[data-locator-results]');
    this.fullList = this.querySelector('[data-locator-full-list]');
    this.radius = this.querySelector('[data-locator-radius]');
    this.radiusOutput = this.querySelector('[data-locator-radius-value]');
    this.country = this.dataset.country || 'US';
    this.selectLabel = this.dataset.selectLabel || 'Make This My Store';
    this.selectedLabel = this.dataset.selectedLabel || 'Your Store';
    this.viewLabel = this.dataset.viewLabel || 'View store';
    this.directionsLabel = this.dataset.directionsLabel || 'Directions';
    // Liquid renders a boolean setting as "true"/"false". Off by default: the
    // button only means something once the header store selector (BOPIS) ships.
    this.showSelect = this.dataset.showSelect === 'true';
    // Pickup wording is a section setting, off until in-store pickup is live.
    this.showPickup = this.dataset.showPickup === 'true';
    this.stores = [];

    // Held so the slider can re-filter without making the shopper retype.
    this.lastLocation = null;

    this.setupRadius();
    this.collapseGroupsOnSmallScreens();
    this.fillTodaysHoursWhenIdle();

    if (this.form) {
      this.form.addEventListener('submit', event => {
        event.preventDefault();
        this.searchByPostal();
      });
    }
    if (this.useLocation) {
      this.useLocation.addEventListener('click', event => {
        event.preventDefault();
        this.searchByGeolocation();
      });
    }
  }

  /*
   * The grouped list renders every <details> open, which is right for no-JS
   * and for desktop. On a phone a hundred stacked cards is a long way to
   * scroll to reach your state, so the groups start closed there and the jump
   * list opens the one you pick.
   */
  collapseGroupsOnSmallScreens() {
    const groups = [...this.querySelectorAll('[data-locator-group]')];
    if (!groups.length) return;
    groups.forEach(group => {
      const toggle = group.querySelector('[data-locator-group-toggle]');
      if (toggle) toggle.addEventListener('click', () => this.setGroupOpen(group, !this.isGroupOpen(group)));
    });
    // Phones only. On desktop the groups are flattened into one grid (see
    // store-locator.css), where collapsing would have nothing to act on.
    if (window.matchMedia('(max-width: 749px)').matches) {
      groups.forEach(group => this.setGroupOpen(group, false));
      // The shopper's own store stays in view: its state starts open.
      const saved = window.TandyStoreSearch && window.TandyStoreSearch.getSelected();
      const savedId = saved && saved.store_id ? String(saved.store_id).toUpperCase() : '';
      if (savedId) {
        const mine = groups.find(group => Array.prototype.some.call(
          group.querySelectorAll('[data-store-id]'),
          card => String(card.getAttribute('data-store-id')).toUpperCase() === savedId
        ));
        if (mine) this.setGroupOpen(mine, true);
      }
    }
    this.setupRegionFilter(groups);
  }

  isGroupOpen(group) {
    const toggle = group.querySelector('[data-locator-group-toggle]');
    return !toggle || toggle.getAttribute('aria-expanded') === 'true';
  }

  setGroupOpen(group, open) {
    const toggle = group.querySelector('[data-locator-group-toggle]');
    const list = group.querySelector('.store-locator__grid');
    if (toggle) toggle.setAttribute('aria-expanded', String(open));
    if (list) list.hidden = !open;
  }

  /*
   * The state links filter the list instead of scrolling to a group: pick
   * Texas and only the Texas stores remain, and the map fits to them. "All"
   * puts everything back. The hash follows the filter (#stores-tx), so a
   * filtered view can be linked to and survives a reload.
   */
  setupRegionFilter(groups) {
    const nav = this.querySelector('[data-locator-filter]');
    if (!nav) return;
    this.filterLinks = [...nav.querySelectorAll('a')];

    // Phones get a dropdown in place of the buttons (see store-locator.css).
    this.filterSelect = nav.querySelector('[data-locator-filter-select]');
    if (this.filterSelect) {
      nav.querySelector('[data-locator-filter-select-wrap]').hidden = false;
      nav.classList.add('has-select');
      this.filterSelect.addEventListener('change', () => {
        const id = this.filterSelect.value;
        this.filterRegion(id, groups);
        history.replaceState(null, '', id ? `#${id}` : location.pathname + location.search);
      });
    }

    this.filterLinks.forEach(link => {
      link.addEventListener('click', event => {
        event.preventDefault();
        const id = link.hasAttribute('data-locator-filter-all') ? '' : link.getAttribute('href').slice(1);
        this.filterRegion(id, groups);
        // replaceState, not a hash assignment: it records the filter in the URL
        // without the browser jumping to the group's anchor.
        history.replaceState(null, '', id ? `#${id}` : location.pathname + location.search);
      });
    });

    // From the URL on load, and again whenever the hash changes without a
    // reload: the back button, or a link to another state's filter.
    const fromHash = () => {
      const id = location.hash.slice(1);
      if (groups.some(group => group.id === id)) this.filterRegion(id, groups);
      else if (!id) this.filterRegion('', groups);
    };
    window.addEventListener('hashchange', fromHash);
    if (location.hash) fromHash();
  }

  async filterRegion(id, groups) {
    // Picking a state ends any postal-code search: its results replace the
    // full list, so leaving them up would show one search's stores under
    // another state's map. Back to the list, and the map follows the state.
    if (this.results && !this.results.hidden) {
      this.results.hidden = true;
      this.results.innerHTML = '';
      if (this.fullList) this.fullList.hidden = false;
      this.lastLocation = null;
    }

    let shown = groups;
    groups.forEach(group => {
      const match = !id || group.id === id;
      group.hidden = !match;
      // A chosen state is always open, even on a phone where groups start closed.
      if (id && match) this.setGroupOpen(group, true);
    });
    if (id) shown = groups.filter(group => group.id === id);

    // The heading says what is on screen: "All Tandy Leather stores (101)"
    // above the Texas stores alone would be wrong.
    const heading = this.querySelector('#store-locator-all');
    if (heading) {
      if (this.allHeading == null) this.allHeading = heading.innerHTML;
      if (id && shown[0]) {
        const region = shown[0].querySelector('[data-locator-group-toggle]').textContent.trim();
        const count = shown[0].querySelectorAll('.store-locator__card').length;
        heading.innerHTML = `Tandy Leather stores in ${StoreLocator.escape(region)} <span class="store-locator__count">(${count})</span>`;
      } else {
        heading.innerHTML = this.allHeading;
      }
    }

    if (this.filterSelect && this.filterSelect.value !== id) this.filterSelect.value = id;

    this.filterLinks.forEach(link => {
      const linkId = link.hasAttribute('data-locator-filter-all') ? '' : link.getAttribute('href').slice(1);
      if (linkId === id) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
    });

    // Fit the map to the stores left in view. The map listens for the same
    // event search results use; nothing here waits on it.
    try {
      await this.loadStores();
    } catch (err) {
      return;
    }
    const ids = new Set(shown.flatMap(group =>
      [...group.querySelectorAll('[data-store-id]')].map(card => card.dataset.storeId)));
    this.announce(this.stores.filter(store => ids.has(String(store.store_id))));
  }

  /*
   * The server-rendered cards cannot show "Today: 10 AM–6 PM": the page is
   * cached, and "today" is the shopper's day. So once the page is idle the
   * directory is loaded (the same file a search would load anyway) and each
   * card's hours are filled in by store id. A failure leaves the cards as they
   * were, which is a complete card without hours.
   */
  fillTodaysHoursWhenIdle() {
    const slots = this.querySelectorAll('[data-locator-full-list] [data-hours-today]');
    if (!slots.length) return;
    const run = async () => {
      try {
        await this.loadStores();
      } catch (err) {
        return;
      }
      const byId = new Map(this.stores.map(store => [String(store.store_id), store]));
      slots.forEach(slot => {
        const card = slot.closest('[data-store-id]');
        const store = card && byId.get(card.dataset.storeId);
        const text = store ? StoreLocator.hoursText(store) : '';
        if (text) {
          slot.textContent = text;
          slot.hidden = false;
        }
      });
    };
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 3000 });
    } else {
      window.setTimeout(run, 1500);
    }
  }

  /*
   * The slider is configured in miles in the section settings, because that is
   * how the merchant thinks about it, but a Canadian shopper should not be
   * dragging a control labelled in miles. So the bounds are converted once, to
   * round numbers in the local unit, rather than showing "64.4 km".
   *
   * The underlying value is metres either way, which is what `nearby` wants.
   */
  setupRadius() {
    if (!this.radius) return;

    // Revealed only now. The markup ships hidden so a no-JS visitor never sees
    // a slider that cannot filter anything.
    const group = this.querySelector('[data-locator-radius-group]');
    if (group) group.hidden = false;

    const miles = key => Number(this.dataset[key]);
    const round = value => Math.round(value / 5) * 5;
    const metric = this.country !== 'US';
    const convert = value => (metric ? round(value * 1.60934) : value);

    this.radius.min = convert(miles('radiusMin') || 10);
    this.radius.max = convert(miles('radiusMax') || 250);
    this.radius.step = convert(miles('radiusStep') || 10);
    this.radius.value = convert(miles('radiusDefault') || 40);

    this.showRadius();
    // `input` for the label so it tracks the thumb; `change` for the search so
    // a drag across the whole range does not re-render at every step.
    this.radius.addEventListener('input', () => this.showRadius());
    this.radius.addEventListener('change', () => {
      if (this.lastLocation) this.render(this.lastLocation);
    });
  }

  showRadius() {
    if (!this.radiusOutput) return;
    const unit = this.country === 'US' ? 'mi' : 'km';
    this.radiusOutput.textContent = `${this.radius.value} ${unit}`;
  }

  /* Slider position in metres. Falls back to the shared default when the
     slider is switched off in the section settings. */
  radiusMeters() {
    if (!this.radius) {
      return Number(this.dataset.radiusMeters)
        || window.TandyStoreSearch.defaultRadiusMeters(this.country);
    }
    const value = Number(this.radius.value);
    return this.country === 'US' ? value * 1609.34 : value * 1000;
  }

  setStatus(message) {
    if (this.status) this.status.textContent = message || '';
  }

  /* Fire-and-forget. Nothing here depends on anyone listening. */
  announce(stores) {
    document.dispatchEvent(new CustomEvent('locator:results', {
      detail: { stores: stores, fit: true },
    }));
  }

  async searchByPostal() {
    const postal = (this.input.value || '').trim();
    if (!postal) return;

    this.setStatus('Searching…');
    let loc = null;
    try {
      const [found] = await Promise.all([
        window.TandyStoreSearch.lookupPostal(postal, this.country, {
          postalSrc: this.dataset.postalSrc,
          directorySrc: this.dataset.directorySrc,
        }),
        this.loadStores(),
      ]);
      loc = found;
    } catch (err) {
      console.error('store-locator: postal lookup failed', err);
    }

    if (!loc) {
      // Never fail silently — a dead-looking button is worse than a message.
      this.lastLocation = null;
      this.setStatus('');
      this.renderMessage(
        "We couldn't find that postal code. Check it and try again, or use your current location."
      );
      return;
    }
    this.render(loc);
  }

  searchByGeolocation() {
    if (!navigator.geolocation) {
      this.renderMessage('Your browser does not support location sharing. Enter a postal code instead.');
      return;
    }
    this.setStatus('Finding your location…');
    navigator.geolocation.getCurrentPosition(
      async position => {
        await this.loadStores();
        this.render({ lat: position.coords.latitude, lng: position.coords.longitude });
      },
      () => {
        this.setStatus('');
        this.renderMessage('We could not read your location. Enter a postal code instead.');
      }
    );
  }

  async loadStores() {
    this.stores = await window.TandyStoreSearch.loadDirectory(this.dataset.directorySrc);
  }

  render(userLoc) {
    this.lastLocation = userLoc;
    const radius = this.radiusMeters();

    // A dedicated locator page showing nothing is a dead end, so fall back to
    // the nearest few rather than an empty result. The header dropdown keeps
    // the strict radius; here the shopper has clearly come to find a store.
    const { results, locatable } = window.TandyStoreSearch.nearby(userLoc, this.stores, {
      maxMeters: radius,
      fallbackNearest: 5,
      limit: 25,
    });

    this.setStatus('');

    if (!locatable) {
      this.renderMessage("We couldn't look up store locations right now. Please try again shortly.");
      return;
    }
    if (!results.length) {
      this.renderMessage('No stores found. Try a different postal code.');
      return;
    }

    const withinRadius = results.filter(s => s.distance_m <= radius).length;
    const heading = withinRadius
      ? `${withinRadius} store${withinRadius === 1 ? '' : 's'} near you`
      : 'No stores nearby — here are the closest';

    this.results.innerHTML =
      `<h2 class="store-locator__results-heading">${heading}</h2>` +
      `<div class="store-locator__grid">${results.map(store => this.card(store)).join('')}</div>`;
    this.results.hidden = false;
    if (this.fullList) this.fullList.hidden = true;
    if (this.showSelect) window.TandyStoreSearch.refreshSelectButtons(this.results);
    this.announce(results);
    this.results.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  renderMessage(message) {
    this.results.innerHTML = `<p class="store-locator__message">${message}</p>`;
    this.results.hidden = false;
    if (this.fullList) this.fullList.hidden = false;
    // The full list is back on screen, so the map goes back to every store.
    // Leaving the previous search's pins up would have the map contradicting
    // the list directly beside it.
    if (this.stores.length) this.announce(this.stores);
  }

  static escape(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, ch => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
    }[ch]));
  }

  /* "18:00" -> "6 PM", matching snippets/store-hours-time.liquid. The stored
     value stays 24-hour because that is what schema.org wants and what sorts
     correctly; only the display changes. Kept identical to the store page's
     version on purpose — a card reading "10:00–18:00" beside a page reading
     "10 AM–6 PM" looks like two different sources of truth. */
  static clock(value) {
    const match = /^(\d{1,2}):(\d{2})/.exec(String(value || '').trim());
    if (!match) return StoreLocator.escape(value);
    const hour = Number(match[1]);
    if (hour > 23) return StoreLocator.escape(value);
    const meridiem = hour >= 12 ? 'PM' : 'AM';
    const shown = hour % 12 === 0 ? 12 : hour % 12;
    return `${shown}${match[2] === '00' ? '' : ':' + match[2]} ${meridiem}`;
  }

  /* Hours are optional: the generated directory only carries them once it is
     rebuilt from the tandy_stores metaobject. Absent hours render nothing
     rather than an empty box. */
  static hoursText(store) {
    /* One implementation, in store-search.js, shared with the header store
       selector: today's hours, "Closed today", or the temporary-closure line. */
    return window.TandyStoreSearch.todayText(store);
  }

  static hoursSummary(store) {
    const text = StoreLocator.hoursText(store);
    return text ? `<p class="store-locator__hours">${text}</p>` : '';
  }

  /* Only when the store offers it, as on the server-rendered cards
     (snippets/store-locator-card.liquid). "Not available yet" on nearly every
     card was the same negative line a hundred times over. */
  static pickupLine(store) {
    return store.pickup === true
      ? '<p class="store-locator__pickup">Order pickup available</p>'
      : '';
  }

  /* Same markup as snippets/store-locator-card.liquid, plus distance. */
  /* The card's two address lines from the directory's one-line address
     ("13900 East Florida Avenue, Unit E, Aurora, CO 80012"): the street
     without its suite or unit, then city, state and ZIP. Same as the server
     card (snippets/store-street.liquid); the full address stays on the store
     page and in the Directions link. */
  static streetOnly(line) {
    const unit = /^(?:#|(?:suite|ste|unit|bldg|building|apt|room|rm|space|spc)\b)/i;
    const words = String(line || '').trim().split(/\s+/);
    const cut = words.findIndex((word, index) => index > 0 && unit.test(word));
    return (cut === -1 ? words : words.slice(0, cut)).join(' ').replace(/,$/, '');
  }

  static cardAddress(address) {
    const parts = String(address || '').split(/,\s*/);
    if (parts.length < 3) return StoreLocator.escape(address || '');
    const street = StoreLocator.streetOnly(parts[0]);
    const city = parts.slice(-2).join(', ');
    return `${StoreLocator.escape(street)}<br>${StoreLocator.escape(city)}`;
  }

  card(store) {
    const esc = StoreLocator.escape;
    const search = window.TandyStoreSearch;
    const place = search.placeName(store.location_name);
    const number = search.storeNumber(store.location_name);
    const distance = search.formatDistance(store.distance_m, this.country);
    const directions = store.address
      ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(store.address)}`
      : `https://www.google.com/maps/dir/?api=1&destination=${store.lat},${store.long}`;

    const name = store.website
      ? `<a href="${esc(store.website)}">${esc(place)}</a>`
      : esc(place);
    const phone = store.phone
      ? `<a class="store-locator__phone" href="tel:${esc(String(store.phone).replace(/[^\d+]/g, ''))}">${esc(store.phone)}</a>`
      : '';
    const view = store.website
      ? `<a class="store-locator__view" href="${esc(store.website)}">${esc(this.viewLabel)}<span class="visually-hidden">: ${esc(place)}</span></a>`
      : '';

    let save = '';
    if (this.showSelect) {
      // Same payload the server-rendered cards use, so the one delegated
      // handler in store-search.js covers both. `location_name` is the exact
      // Location name, not the display name: the pickup badge matches on it.
      const payload = esc(JSON.stringify({
        location_name: store.location_name,
        store_id: store.store_id,
        address: store.address,
        phone: store.phone || '',
        lat: store.lat,
        long: store.long,
        website: store.website || '',
      }));
      save = `<div class="store-locator__card-save"><button type="button" class="store-locator__select"
          data-store-select="${payload}"
          data-store-name="${esc(store.location_name)}"
          data-store-distance="${esc(distance)}"
          data-label="${esc(this.selectLabel)}"
          data-label-selected="${esc(this.selectedLabel)}"
          aria-pressed="false">${esc(this.selectLabel)}</button></div>`;
    }

    return `
      <article class="store-locator__card" data-store-id="${esc(store.store_id)}">
        <div class="store-locator__card-head">
          <h3 class="store-locator__name">${name}</h3>
          <span class="store-locator__distance">${esc(distance)}</span>
        </div>
        ${number ? `<p class="store-locator__number">Store #${esc(number)}</p>` : ''}
        <address class="store-locator__address">${StoreLocator.cardAddress(store.address)}</address>
        ${StoreLocator.hoursSummary(store)}
        ${this.showPickup ? StoreLocator.pickupLine(store) : ''}
        ${phone}
        <div class="store-locator__card-actions">
          ${view}
          <a class="store-locator__directions" href="${directions}" target="_blank" rel="noopener">${esc(this.directionsLabel)}<span class="visually-hidden"> to ${esc(place)} (opens in a new tab)</span></a>
        </div>
        ${save}
      </article>`;
  }
}

!customElements.get('store-locator') && customElements.define('store-locator', StoreLocator);

